import { useCallback, useEffect, useState } from "react";
import * as yup from "yup";

import useTabsValidation from "@/hooks/useTabsValidation";
import { useFormStore } from "@/store/form";

// Components
import Layout from "@/components/Layout";

import { sendStepUserJourney, sendUserJourney } from "@/api/userJourney";
import Button from "@/components/Button";
import Column from "@/components/Column";
import Columns from "@/components/Columns";
import Form from "@/components/Form";
import Input from "@/components/Input";
import PageHeader from "@/components/PageHeader";
import SEO from "@/components/SEO";
import usePageSubmit from "@/hooks/usePageSubmit";
import { useProductStore } from "@/store/product";
import { getOnlyNumbers } from "@/utils";
import { formatPrice } from "@/utils/currency";
import {
  removeAccentAndSpaceAndSetLowerCase,
  removeAccentsAndSetUpperCase,
} from "@/utils/string";
import { GenericObject } from "@/utils/types";
import { validateDate } from "@/utils/validators/date";
import { useTagManager } from "@/hooks/useTagManager";

interface PageValidationSchema {
  fullName: yup.StringSchema;
  birthDate: yup.StringSchema;
  motherName: yup.StringSchema;

  [key: string]: yup.StringSchema;
}

// Page Schema
const personalDataSchema = yup.object().shape({
  fullName: yup.string().trim().required("O nome é obrigatório"),
  birthDate: yup
    .string()
    .test("date", "Data inválida", function (value) {
      // @ts-expect-error - TS doesn't recognize the context of the function
      return validateDate.call(this, value, [18, 120]);
    })
    .required("A data de nascimento é obrigatória"),
  motherName: yup.string().trim().required("O nome da mãe é obrigatório"),
});

const pageValidationSchema: PageValidationSchema = {
  fullName: yup.string().required("O nome é obrigatório"),
  birthDate: yup
    .string()
    .test("date", "Data inválida", function (value) {
      // @ts-expect-error - TS doesn't recognize the context of the function
      return validateDate.call(this, value, [18, 120]);
    })
    .required("A data de nascimento é obrigatória"),
  motherName: yup.string().required("O nome da mãe é obrigatório"),
};

// Page Config
const pageConfig = {
  route: "/personal-data/complement",
  prevRoute: "/personal-data",
  nextRoute: "/type-product",
};

// Personal Data Complement
export function PersonalDataComplement() {
  const { dataLayer } = useTagManager();
  useTabsValidation(
    pageConfig.route,
    // isTableOrLess ? prevMobileRoute :
    pageConfig.prevRoute
  );

  const form = useFormStore((store) => store.value);
  const [fullName, setFullName] = useState(form.fullName);
  const [birthDate, setBirthDate] = useState(form.birthDate);
  const [motherName, setMotherName] = useState(form.motherName);
  const [formIsValid, setFormaIsValid] = useState(false);
  const product = useProductStore((store) => store.item);
  const currentPrice = useProductStore((store) =>
    store.getCurrentPrice(getOnlyNumbers(form.tel).slice(0, 2))
  );
  const loyalty = useProductStore((store) => store.loyalty);
  const totalGbProduct = useProductStore((store) => store.getTotalGbProduct());
  const addData = useFormStore((store) => store.addData);
  const saleId = useFormStore((store) => store.saleId);

  const submitCallback = usePageSubmit(pageConfig.nextRoute);
  const handleOnSubmit = (formData: GenericObject) => {
    dataLayer({
      event: "step",
      passo: "DADOS-PESSOAIS-COMPLEMENTO",
      sku: removeAccentsAndSetUpperCase(product?.tim_id),
      nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
        product?.nome
      )}-${totalGbProduct}`,
      valor: formatPrice(currentPrice),
      status: "Sucesso",
    });
    submitCallback(formData);
  };

  const handleChangeFullName = useCallback((value: string) => {
    setFullName(value);
  }, []);

  useEffect(() => {
    personalDataSchema
      .isValid({
        fullName,
        birthDate,
        motherName,
      })
      .then((isValid) => {
        setFormaIsValid(isValid);
      })
      .catch(() => {
        setFormaIsValid(false);
      });
    return setFormaIsValid(formIsValid);
  }, [fullName, birthDate, motherName, formIsValid]);

  const handleUserJourney = useCallback(
    (name: string, value: string) => {
      addData({
        [name]: value,
      });
      sendUserJourney({
        ...form,
        product: product!,
        loyalty,
        ...{
          [name]: value,
          passo: "DADOS_PESSOAIS_COMPLEMENTO",
        },
      });
    },
    [addData, form, product, loyalty]
  );

  const handleOnBlurUserJourney = useCallback(
    (name: string, value: string) => {
      pageValidationSchema[name]
        .isValid({
          [name]: value,
        })
        .then((isValid: boolean) => {
          if (isValid && form[name] !== value) {
            handleUserJourney(name, value);
          }
        });
    },
    [form, handleUserJourney]
  );

  const onClickForwardButton = () => {
    sendStepUserJourney({
      passo: "ENDERECO",
      uuid: form.uuid,
    });
  };

  return (
    <Layout>
      <SEO
        title="Dados Pessoais - Complemento"
        description="Adicione os dados pessoais do titular da conta para continuar comprando seu plano TIM!"
      />

      <Column size={12} className="text-center">
        <PageHeader
          title="Precisamos de mais algumas informações suas para prosseguir."
          dataIcTitle="sec-omega-comunicacao"
        />
      </Column>

      <Columns
        as={Form}
        onSubmit={handleOnSubmit}
        validationSchema={personalDataSchema}
        className="pt-6"
        justifyContent="center"
        alignItems="start"
      >
        <Column size={12} className="pb-6">
          <Input
            name="fullName"
            label="Nome Completo"
            placeholder="Nome Completo"
            onChange={(e) => handleChangeFullName(e.target.value)}
            onBlur={(e) => {
              handleChangeFullName(e.target.value);
              handleOnBlurUserJourney(e.target.name, e.target.value);
            }}
            value={fullName}
            disabled={!!saleId}
          />
        </Column>

        <Column size={12} className="pb-6">
          <Input
            type="tel"
            mask="99/99/9999"
            name="birthDate"
            label="Data de Nascimento"
            placeholder="24/07/2000"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            onBlur={(e) => {
              setBirthDate(e.target.value);
              handleOnBlurUserJourney(e.target.name, e.target.value);
            }}
            disabled={!!saleId}
          />
        </Column>

        <Column size={12} className="pb-6">
          <Input
            name="motherName"
            label="Nome da Mãe"
            placeholder="Nome da Mãe"
            value={motherName}
            onChange={(e) => setMotherName(e.target.value)}
            onBlur={(e) => {
              setMotherName(e.target.value);
              handleOnBlurUserJourney(e.target.name, e.target.value);
            }}
            disabled={!!saleId}
          />
        </Column>
        <Column size={12}>
          <Button
            type="submit"
            large
            helperText={!formIsValid ? "todos os campos são obrigatórios" : ""}
            disabled={!formIsValid}
            onClick={() => onClickForwardButton()}
          >
            {!formIsValid ? "Preencha os campos para continuar" : "Continuar"}
          </Button>
        </Column>
        <Column size={12} className="mt-8">
          <Button
            type="button"
            kind="secondary"
            large
            to={pageConfig.prevRoute}
            data-ic-trigger="sec-omega-voltar"
          >
            Voltar
          </Button>
        </Column>
      </Columns>
    </Layout>
  );
}

export default PersonalDataComplement;
